import type { CreateTeamResponse } from "@/hooks/useTeams"

function formatEndDate(iso: string | null) {
  if (!iso) return "no end date"
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime())
    ? iso
    : `until ${parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
}

export function describeCreateTeamAccess(result: CreateTeamResponse) {
  if (result.organizationAccess || result.trialStarted?.kind === "organization_inherited")
    return "covered by the organization subscription"
  if (result.comp?.kind === "granted") {
    return `comped ${result.comp.planId} (${formatEndDate(result.comp.expiresAt)})`
  }
  if (result.comp?.kind === "failed") {
    return `comp NOT applied: ${result.comp.message}`
  }

  switch (result.trialStarted?.kind) {
    case "trial_created":
      return "trial started"
    case "orphan_activated":
      return "pending trial activated"
    case "skipped_existing_active":
      return "existing active subscription found"
    case "trial_failed":
      return "trial failed to start"
    default:
      return "no plan (free tier; comp or trial later from the team page)"
  }
}

export function describeCreateTeamTrialCancellation(result: CreateTeamResponse) {
  if (result.trialCancellation.kind !== "cancelled") {
    return ""
  }

  return `cancelled creator's trial${
    result.trialCancellation.stripeCancelled ? " (Stripe + DB)" : " (DB only)"
  }`
}

/**
 * The toast for a create-team result. A team whose comp failed is created but
 * not what the admin asked for, so it is a destructive toast that says so,
 * rather than a green "Team created" with the failure buried in the detail.
 */
export function createTeamToast(
  result: CreateTeamResponse,
  lead: string
): { title: string; description: string; variant?: "destructive" } {
  const accessMsg = describeCreateTeamAccess(result)
  const trialMsg = describeCreateTeamTrialCancellation(result)
  // Our billing row already says cancelled, so nothing will retry this; the
  // admin is the only one left who can finish it in Stripe. It has to survive
  // every branch below, including a failed comp — Comp Plan can't find the
  // trial again either.
  const trial = result.trialCancellation
  const stripeRefused =
    trial.kind === "cancelled" && !trial.stripeCancelled
      ? `Stripe did not confirm cancelling trial ${trial.stripeSubscriptionId ?? "(no Stripe id)"}; our record already says cancelled, so check it in the Stripe dashboard and cancel it by hand if it is still live.`
      : ""
  if (result.comp?.kind === "failed") {
    return {
      title: "Team created, but not comped",
      description: [
        lead,
        result.comp.message,
        "Open the team and use Comp Plan to finish.",
        stripeRefused,
      ]
        .filter(Boolean)
        .join(" · "),
      variant: "destructive",
    }
  }
  if (stripeRefused) {
    return {
      title: "Team created, but the coach's trial may still be live in Stripe",
      description: [lead, accessMsg, stripeRefused].filter(Boolean).join(" · "),
      variant: "destructive",
    }
  }
  return {
    title: "Team created",
    description: [lead, accessMsg, trialMsg].filter(Boolean).join(" · "),
  }
}
